if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.mql = window.matchMedia('(min-width: 750px)');
        this.listenerController = null;
        this.refreshFrame = null;

        this.onClick = this.onClick.bind(this);
        this.onSlideChanged = this.onSlideChanged.bind(this);
        this.onGalleryUpdated = this.onGalleryUpdated.bind(this);

        this.onSlideChangedDebounced =
          typeof window.debounce === 'function'
            ? window.debounce(this.onSlideChanged, 150)
            : this.onSlideChanged;
      }

      connectedCallback() {
        this.refresh();
      }

      disconnectedCallback() {
        this.listenerController?.abort();
        this.listenerController = null;

        if (this.refreshFrame) {
          cancelAnimationFrame(this.refreshFrame);
          this.refreshFrame = null;
        }
      }

      refresh() {
        this.listenerController?.abort();
        this.listenerController = new AbortController();

        const signal = this.listenerController.signal;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        if (!this.elements.viewer) return;

        /*
         * Clique delegado: continua funcionando mesmo quando o módulo do kit
         * substitui todo o conteúdo interno do <media-gallery>.
         */
        this.addEventListener('click', this.onClick, { signal });

        this.elements.viewer.addEventListener(
          'slideChanged',
          this.onSlideChangedDebounced,
          { signal }
        );

        document.addEventListener(
          'ecomhard-kit:gallery-updated',
          this.onGalleryUpdated,
          { signal }
        );

        this.scheduleSliderRefresh();

        if (
          this.dataset.desktopLayout?.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
          requestAnimationFrame(() => this.removeListSemantic());
        }

        const activeMedia =
          this.elements.viewer.querySelector(
            '.is-active[data-media-id]'
          ) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (activeMedia?.dataset.mediaId) {
          const activeThumbnail = this.elements.thumbnails?.querySelector(
            `[data-target="${activeMedia.dataset.mediaId}"]`
          );

          this.setActiveThumbnail(activeThumbnail);
        }
      }

      scheduleSliderRefresh() {
        if (this.refreshFrame) {
          cancelAnimationFrame(this.refreshFrame);
        }

        this.refreshFrame = requestAnimationFrame(() => {
          this.refreshFrame = requestAnimationFrame(() => {
            this.refreshFrame = null;
            this.resetSliderComponents();
          });
        });
      }

      resetSliderComponents() {
        [this.elements?.viewer, this.elements?.thumbnails]
          .filter(Boolean)
          .forEach((sliderComponent) => {
            if (typeof sliderComponent.resetPages === 'function') {
              sliderComponent.resetPages();
            }

            if (typeof sliderComponent.initPages === 'function') {
              sliderComponent.initPages();
            }
          });
      }

      onClick(event) {
        const button = event.target.closest?.(
          '[data-target] button'
        );

        if (!button || !this.contains(button)) return;

        const thumbnail = button.closest('[data-target]');
        const mediaId = thumbnail?.dataset.target;

        if (!mediaId) return;

        event.preventDefault();
        this.setActiveMedia(mediaId, false);
      }

      onGalleryUpdated(event) {
        const sectionId = String(event.detail?.sectionId || '');
        const ownSectionId = String(this.id || '').replace(
          'MediaGallery-',
          ''
        );

        if (sectionId && ownSectionId && sectionId !== ownSectionId) {
          return;
        }

        requestAnimationFrame(() => {
          this.refresh();
        });
      }

      onSlideChanged(event) {
        const currentElement = event.detail?.currentElement;
        const mediaId = currentElement?.dataset?.mediaId;

        if (!mediaId || !this.elements?.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend = false) {
        /*
         * Reconsulta os elementos para cobrir atualizações feitas antes do
         * evento customizado de conclusão do módulo do kit.
         */
        if (!this.elements?.viewer || !this.contains(this.elements.viewer)) {
          this.elements = {
            liveRegion: this.querySelector('[id^="GalleryStatus"]'),
            viewer: this.querySelector('[id^="GalleryViewer"]'),
            thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          };
        }

        const viewer = this.elements?.viewer;
        if (!viewer) return;

        const activeMedia =
          viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement?.firstChild !== activeMedia) {
            activeMedia.parentElement?.prepend(activeMedia);
          }

          const activeThumbnail =
            this.elements.thumbnails?.querySelector(
              `[data-target="${mediaId}"]`
            );

          if (
            activeThumbnail &&
            activeThumbnail.parentElement?.firstChild !== activeThumbnail
          ) {
            activeThumbnail.parentElement?.prepend(activeThumbnail);
          }

          viewer.resetPages?.();
          this.elements.thumbnails?.resetPages?.();
        }

        this.preventStickyHeader();

        requestAnimationFrame(() => {
          const mediaList =
            viewer.slider || activeMedia.parentElement;

          if (
            mediaList &&
            typeof mediaList.scrollTo === 'function'
          ) {
            mediaList.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: 'smooth',
            });
          } else if (mediaList) {
            mediaList.scrollLeft = activeMedia.offsetLeft;
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          if (activeMediaRect.top <= -0.5) {
            window.scrollTo({
              top: activeMediaRect.top + window.scrollY,
              behavior: 'smooth',
            });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        const thumbnails = this.elements?.thumbnails;

        if (!thumbnails || !thumbnail) return;

        thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute('aria-current');
          });

        thumbnail
          .querySelector('button')
          ?.setAttribute('aria-current', 'true');

        if (
          typeof thumbnails.isSlideVisible === 'function' &&
          thumbnails.isSlideVisible(thumbnail, 10)
        ) {
          return;
        }

        const thumbnailList =
          thumbnails.slider || thumbnail.parentElement;

        if (
          thumbnailList &&
          typeof thumbnailList.scrollTo === 'function'
        ) {
          thumbnailList.scrollTo({
            left: thumbnail.offsetLeft,
            behavior: 'smooth',
          });
        } else if (thumbnailList) {
          thumbnailList.scrollLeft = thumbnail.offsetLeft;
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        const liveRegion = this.elements?.liveRegion;

        if (!image || !liveRegion || !position) return;

        const announce = () => {
          const message =
            window.accessibilityStrings?.imageAvailable;

          if (!message) return;

          liveRegion.setAttribute('aria-hidden', 'false');
          liveRegion.innerHTML = message.replace(
            '[index]',
            position
          );

          window.setTimeout(() => {
            liveRegion.setAttribute('aria-hidden', 'true');
          }, 2000);
        };

        if (image.complete) {
          announce();
        } else {
          image.addEventListener('load', announce, {
            once: true,
          });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        deferredMedia?.loadContent?.(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        const viewer = this.elements?.viewer;

        if (!viewer?.slider) return;

        viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        viewer.sliderItems?.forEach((slide) => {
          slide.setAttribute('role', 'presentation');
        });
      }
    }
  );
}
